import {
    MenuItem,
    Stack,
    StandardTextFieldProps,
    SxProps,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";
import HelperInputText from "../text/HelperInputText";
import { getCouponFieldStyle } from "../Checkout/CouponStyles";
import { useTheme } from "../../util/themes";

export interface ControlledTextFieldProps extends StandardTextFieldProps {
    control: Control<any>;
    options?: any[];
    currency?: boolean;
    percentage?: boolean;
    onFocus?: (event: any) => void;
    currencyPrefix?: string;
    currencySuffix?: string;
    roundDecimals?: boolean;
    inputSx?: SxProps;
    disabled?: boolean;
    textAlign?: "left" | "right" | "center";
}

export const ControlledTextField = ({
    helperText,
    label,
    required,
    sx,
    style,
    ...props
}: ControlledTextFieldProps) => {
    const theme = useTheme();
    const couponFieldStyle = getCouponFieldStyle(theme as any);

    const labelRef = useRef(null);
    const [isFocused, setFocused] = useState<boolean>(false);

    const handleFocus = (event: any) => {
        setFocused(true);
        props?.onFocus && props?.onFocus(event);
    };

    const handleBlur = () => setFocused(false);

    const valueIsValid = (value: any) => {
        if (props.currency && (!!value || value == 0)) return true;
        if (value === null || value == "" || !value) return false;

        if (props.select) return value > 0;

        return true;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (props.type === "number") {
            const allowedKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
                "Tab",
                "Enter",
                "Escape",
                "Home",
                "End",
            ];

            if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
                return;
            }

            if (!/^[0-9]$/.test(event.key)) {
                event.preventDefault();
            }
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const maxLengthRaw = (props.inputProps as any)?.maxLength;
        const maxLength =
            typeof maxLengthRaw === "number"
                ? maxLengthRaw
                : typeof maxLengthRaw === "string"
                  ? parseInt(maxLengthRaw, 10)
                  : undefined;

        if (maxLength && event.target.value && event.target.value.length > maxLength) {
            event.target.value = event.target.value.slice(0, maxLength);
        }

        if (props.type === "number") {
            const numericValue = event.target.value.replace(/[^0-9]/g, "");

            if (event.target.value !== numericValue) {
                event.target.value = numericValue;
            }
        }
    };

    return (
        <Controller
            control={props.control}
            name={props.name || ""}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Stack sx={{ width: "100%" }}>
                    <TextField
                        {...(() => {
                            const {
                                roundDecimals,
                                currency,
                                percentage,
                                currencyPrefix,
                                currencySuffix,
                                inputSx,
                                textAlign,
                                ...textFieldProps
                            } = props;
                            return textFieldProps;
                        })()}
                        sx={couponFieldStyle}
                        label={
                            <Stack
                                direction="row"
                                ref={labelRef}
                                justifyContent="flex-start"
                                spacing={0.4}
                            >
                                <div>{label}</div>
                                <Typography sx={{ color: "#d32f2f" }}>
                                    {required ? "*" : ""}
                                </Typography>
                            </Stack>
                        }
                        variant="outlined"
                        type={props.type}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onInput={handleInputChange}
                        value={
                            props.type === "number"
                                ? value != null
                                    ? +value
                                    : ""
                                : !props.select
                                  ? (value ?? "")
                                  : !props.options || !value
                                    ? 0
                                    : props.percentage && !value
                                      ? 9999
                                      : value
                        }
                        onChange={(e) => {
                            if (props.percentage || props.currency || props.type === "number")
                                onChange(e.target.value === "" ? null : e.target.value);
                            else onChange(e);

                            if (props.onChange) props.onChange(e);
                        }}
                        error={!!error}
                        defaultValue={props.defaultValue}
                        inputProps={{
                            prefix: props.currency
                                ? props?.currencyPrefix === undefined
                                    ? "$"
                                    : props.currencyPrefix
                                : undefined,
                            suffix: props.percentage ? "%" : props?.currencySuffix,
                            roundDecimals: props.roundDecimals,
                            style: {
                                textAlign: props?.textAlign,
                            },
                            ...props.inputProps,
                        }}
                        InputLabelProps={{
                            shrink:
                                isFocused ||
                                Boolean(value !== 0 || props.select ? value : value + 1),
                            ...props.InputLabelProps,
                        }}
                        SelectProps={{
                            MenuProps: {
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                },
                            },
                        }}
                    >
                        {props.select && <MenuItem value={0} disableRipple />}
                        {props.options &&
                            props.select &&
                            props.options.map((option) => (
                                <MenuItem key={option.id} value={option.id} disableRipple>
                                    {option.descripcion}
                                </MenuItem>
                            ))}
                    </TextField>

                    {error && <HelperInputText text={error.message} error />}

                    {helperText && !error && <HelperInputText text={helperText} />}
                </Stack>
            )}
        />
    );
};
